import { CaretDownOutlined } from '@ant-design/icons'
import { GraviButton, MaterialIcon, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Form, InputNumber, Popover, Select, Switch, Tooltip } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import type { FormInstance } from 'antd/lib'
import { getGroupingOptions, getSiteOptions, parseSelectButtonText, resetFormFields } from './util'
import { IconInfoCircle } from '@tabler/icons-react'
import type { PriceManagementMetadata, SupplyDirectoryHeaderFilters } from '@/components/SupplyManagement/types'
import type { ComparePriceFiltersForm } from '../../types'

type ComparePricesPopoverProps = {
  metadata: PriceManagementMetadata
  filters: SupplyDirectoryHeaderFilters
  setFilters: (filters: SupplyDirectoryHeaderFilters) => void
}

export function ComparePricesPopover({ metadata, filters, setFilters }: ComparePricesPopoverProps) {
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm<ComparePriceFiltersForm>()

  useEffect(() => {
    resetFormFields(form, filters)
  }, []) // initialize form fields on render

  const handleFinish = (values: ComparePriceFiltersForm) => {
    if (values?.groupBy === 'site') {
      // ignore ts error
      setFilters({ ...filters, ...values })
    } else {
      setFilters({ ...filters, ...values, siteInfo: undefined })
      form.setFieldsValue({ siteInfo: undefined })
    }
    setOpen(false)
  }

  function handleCancel() {
    setOpen(false)
    resetFormFields(form, filters)
  }

  function handleOpenChange(newOpen: boolean) {
    if (!newOpen) {
      handleCancel()
    } else {
      setOpen(true)
    }
  }

  const selectFilterButtonText = useMemo(
    () => parseSelectButtonText(filters?.groupBy, filters?.siteInfo?.siteOptionJSON),
    [filters?.groupBy, filters?.siteInfo?.siteOptionJSON]
  )

  return (
    <Popover
      content={
        <ComparePriceForm
          metadata={metadata}
          filters={filters}
          handleFinish={handleFinish}
          handleCancel={handleCancel}
          form={form}
        />
      }
      title={
        <Horizontal>
          <Texto category='h5'>Compare Prices To:</Texto>
        </Horizontal>
      }
      trigger='click'
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Tooltip title='Compare Prices By'>
        <GraviButton
          iconPosition='start'
          style={{ textTransform: 'capitalize' }}
          icon={<MaterialIcon icon='text_compare' size={14} style={{ verticalAlign: 'middle' }} />}
          buttonText={
            <Horizontal gap={4} verticalCenter>
              <Texto>{selectFilterButtonText}</Texto>
              <CaretDownOutlined className='ml-1' />
            </Horizontal>
          }
        />
      </Tooltip>
    </Popover>
  )
}

type ComparePriceFormProps = {
  metadata: PriceManagementMetadata
  filters: SupplyDirectoryHeaderFilters
  handleFinish: (values: ComparePriceFiltersForm) => void
  handleCancel: () => void
  form: FormInstance<ComparePriceFiltersForm>
}

function ComparePriceForm({ metadata, filters, handleFinish, handleCancel, form }: ComparePriceFormProps) {
  const groupingOptions = useMemo(() => getGroupingOptions(metadata?.groupings), [metadata?.groupings])

  const siteOptions = useMemo(() => getSiteOptions(metadata?.stores), [metadata?.stores])

  function resetSiteInfo(setFieldValue: (name: any, value: any) => void) {
    setFieldValue(['siteInfo', 'siteNumber'], undefined)
    setFieldValue(['siteInfo', 'onlyInNetworkTerminals'], true)
  }

  return (
    <Form form={form} layout='vertical' onFinish={handleFinish} style={{ width: 280 }}>
      <Horizontal verticalCenter className='mt-3 mb-1' style={{ minWidth: 260 }} justifyContent='space-between'>
        <Texto appearance='hint'>Other Prices At: </Texto>
        <Form.Item name='groupBy' initialValue={filters?.groupBy || 'terminal'} noStyle>
          <Select style={{ minWidth: 120 }} options={groupingOptions} />
        </Form.Item>
      </Horizontal>

      <Form.Item shouldUpdate={(prevValues, curValues) => prevValues.groupBy !== curValues.groupBy} noStyle>
        {({ getFieldValue, setFieldValue }) => {
          const groupBySite = getFieldValue('groupBy') === 'site'
          if (!groupBySite) {
            resetSiteInfo(setFieldValue)
            return null
          }
          return (
            <Vertical verticalCenter className='mt-3' justifyContent='space-between'>
              <Texto appearance='hint'>Site (Required): </Texto>
              <Form.Item
                name={['siteInfo', 'siteOptionJSON']}
                style={{ marginBottom: 4 }}
                rules={[{ required: true, message: 'Select site to continue' }]}
              >
                <Select style={{ minWidth: 120 }} options={siteOptions} showSearch />
              </Form.Item>
              <Horizontal verticalCenter className='p-1'>
                <Form.Item name={['siteInfo', 'onlyInNetworkTerminals']} noStyle initialValue={true}>
                  <Switch defaultChecked />
                </Form.Item>
                <Vertical className='ml-2'>
                  <Texto appearance='hint'> Only In-Network Terminals</Texto>
                </Vertical>
              </Horizontal>
            </Vertical>
          )
        }}
      </Form.Item>
      <Texto appearance='hint' style={{ fontStyle: 'italic' }} className='mt-3'>
        <IconInfoCircle size={12} className='mr-1' />
        Total Price, Freight, Loaded Miles and Mins columns will only show when a site is selected.
      </Texto>
      <Horizontal verticalCenter className='mt-3 pb-2' justifyContent='space-between'>
        <Texto appearance='hint'>Price Limit: </Texto>
        <Form.Item name='priceLimit' initialValue={filters?.priceLimit || 5} noStyle>
          <InputNumber min={1} />
        </Form.Item>
      </Horizontal>
      <Horizontal
        style={{ borderTop: '1px solid var(--gray-100)' }}
        verticalCenter
        justifyContent='space-between'
        className='mt-2'
      >
        <GraviButton
          buttonText='Cancel'
          style={{ background: 'transparent' }}
          onClick={handleCancel}
          className='mt-2'
        />
        <GraviButton theme2 buttonText='Save' htmlType='submit' className='mt-2' />
      </Horizontal>
    </Form>
  )
}
